import { useCommentService } from '@src/hooks/useCommentService';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ClipCommentPreview, ClipRef } from '@src/types.ts/comment';

const logger = LogManager.getLogger('hook:useClipCommentCounts');

export interface ClipCommentThreadSummary {
  open: number;
  resolved: number;
}

export interface ClipCommentCounts {
  /** Summed comment count across the given video records; 0 when none/unknown. */
  getCount: (videoIds: string[]) => number;
  /** Open/resolved thread totals across the given video records. */
  getThreadSummary: (videoIds: string[]) => ClipCommentThreadSummary;
  /** Most recent comment across the given video records; `null` when none. */
  getLatest: (videoIds: string[]) => ClipCommentPreview | null;
  /** Refetch counts/previews — call after the user may have added comments. */
  refresh: () => void;
  /**
   * Optimistically nudge a video's cached count (e.g. +1 on post, -1 on delete)
   * so the card reflects a change made in the modal before the next refetch.
   */
  applyDelta: (videoId: string | undefined, delta: number) => void;
}

/**
 * Fetches the comment count for a batch of Video records in one request and
 * exposes a per-video lookup, for the clips-list comment indicator. A card sums
 * across its records (primary + associated). The fetch re-runs when the set of
 * video ids changes, or when `refresh()` is called (e.g. after the clip-preview
 * modal closes, where comments may have been added). Callers gate on the
 * comments feature flag by passing an empty `refs` array when comments are off.
 */
export function useClipCommentCounts(refs: ClipRef[]): ClipCommentCounts {
  const service = useCommentService();
  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const [threads, setThreads] = useState<Map<string, ClipCommentThreadSummary>>(
    new Map(),
  );
  const [latest, setLatest] = useState<Map<string, ClipCommentPreview>>(
    new Map(),
  );
  const [refreshToken, setRefreshToken] = useState(0);
  const refresh = useCallback(() => setRefreshToken(t => t + 1), []);

  const applyDelta = useCallback(
    (videoId: string | undefined, delta: number) => {
      if (!videoId || delta === 0) return;
      setCounts(prev => {
        const next = new Map(prev);
        next.set(videoId, Math.max(0, (next.get(videoId) ?? 0) + delta));
        return next;
      });
    },
    [],
  );

  // Stable signature so the effect ignores new array identities each render.
  const signature = useMemo(
    () =>
      Array.from(new Set(refs.map(ref => ref.videoId).filter(Boolean)))
        .sort()
        .join('|'),
    [refs],
  );

  useEffect(() => {
    if (!signature) {
      setCounts(new Map());
      setThreads(new Map());
      setLatest(new Map());
      return;
    }
    const validRefs = signature.split('|').map(videoId => ({ videoId }));

    let cancelled = false;
    void service.countByClips(validRefs).then(result => {
      if (cancelled) return;
      if (!result.ok) {
        logger.warn('Failed to load clip comment counts', result.error);
        return;
      }
      const nextCounts = new Map<string, number>();
      const nextThreads = new Map<string, ClipCommentThreadSummary>();
      const nextLatest = new Map<string, ClipCommentPreview>();
      for (const entry of result.value) {
        nextCounts.set(entry.videoId, entry.count);
        nextThreads.set(entry.videoId, {
          open: entry.openThreads,
          resolved: entry.resolvedThreads,
        });
        if (entry.latest) nextLatest.set(entry.videoId, entry.latest);
      }
      setCounts(nextCounts);
      setThreads(nextThreads);
      setLatest(nextLatest);
    });

    return () => {
      cancelled = true;
    };
  }, [signature, service, refreshToken]);

  return {
    getCount: videoIds =>
      videoIds.reduce((sum, id) => sum + (counts.get(id) ?? 0), 0),
    getThreadSummary: videoIds =>
      videoIds.reduce(
        (sum, id) => {
          const t = threads.get(id);
          if (t) {
            sum.open += t.open;
            sum.resolved += t.resolved;
          }
          return sum;
        },
        { open: 0, resolved: 0 },
      ),
    getLatest: videoIds => {
      let best: ClipCommentPreview | null = null;
      for (const id of videoIds) {
        const l = latest.get(id);
        if (l && (!best || l.createdAt > best.createdAt)) best = l;
      }
      return best;
    },
    refresh,
    applyDelta,
  };
}
